import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { authFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Phone, ChevronLeft, ChevronRight, Download, FileSpreadsheet, Settings2, Save,
         AlertTriangle, ArrowLeftRight, X, Plus, Search } from "lucide-react";

const GIORNI = ["dom", "lun", "mar", "mer", "gio", "ven", "sab"];

// Data locale, non UTC: toISOString sposta il giorno fra mezzanotte e le due,
// che per la reperibilita' e' proprio l'ora in cui si guarda chi risponde.
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const agg = (dataIso, giorni) => {
  const d = new Date(`${dataIso}T00:00:00`);
  d.setDate(d.getDate() + giorni);
  return iso(d);
};
/** Il venerdi' da cui parte il turno che contiene questo giorno. */
const inizioTurno = (dataIso) => {
  const d = new Date(`${dataIso}T00:00:00`);
  return agg(dataIso, -(((d.getDay() + 2) % 7)));
};
const breve = (dataIso) => {
  const d = new Date(`${dataIso}T00:00:00`);
  return `${d.getDate()} ${d.toLocaleDateString("it-IT", { month: "short" })}`;
};

export default function Reperibilita() {
  const { user } = useAuth();
  const [da, setDa] = useState(() => inizioTurno(iso(new Date())));
  // «6» vuol dire sei turni, come si e' sempre fatto. Le altre quattro voci
  // sono i periodi di calendario - mese, trimestre, semestre, anno - quelli
  // che si consegnano al committente e si appendono in bacheca.
  const [settimane, setSettimane] = useState(6);
  const [periodo, setPeriodo] = useState("");        // "" = a turni
  const [estremi, setEstremi] = useState(null);      // {dal, al, etichetta}
  const [dati, setDati] = useState(null);
  const [adesso, setAdesso] = useState(null);
  const [mostraImp, setMostraImp] = useState(false);
  const [bozzaImp, setBozzaImp] = useState(null);
  const [scelta, setScelta] = useState(null);   // { turno, ruolo }
  const [scambio, setScambio] = useState(null); // assegnazione da cui cedere

  // Il periodo di calendario lo calcola il server, che sa dove cadono i
  // trimestri: qui si tiene solo la data di riferimento.
  useEffect(() => {
    if (!periodo) { setEstremi(null); return; }
    let vivo = true;
    api.get(`/reperibilita/periodi?riferimento=${da}`)
      .then(({ data }) => {
        const scelto = data.find((x) => x.codice === periodo);
        if (vivo && scelto) setEstremi(scelto);
      })
      .catch(() => { if (vivo) setEstremi(null); });
    return () => { vivo = false; };
  }, [periodo, da]);

  const dal = periodo ? (estremi?.dal || da) : da;
  const al = useMemo(
    () => (periodo ? (estremi?.al || da) : agg(da, settimane * 7 - 1)),
    [periodo, estremi, da, settimane]);

  // Avanti e indietro si muovono di quanto si sta guardando: di un mese se
  // guardi il mese, di sei turni se guardi sei turni.
  const passo = (verso) => {
    if (!periodo) { setDa(agg(da, verso * 7 * settimane)); return; }
    const mesi = { mese: 1, trimestre: 3, semestre: 6, anno: 12 }[periodo] || 1;
    const d = new Date(`${dal}T00:00:00`);
    d.setMonth(d.getMonth() + verso * mesi);
    setDa(iso(d));
  };

  const carica = useCallback(async () => {
    const [g, a] = await Promise.all([
      api.get(`/reperibilita/griglia?dal=${dal}&al=${al}`),
      api.get("/reperibilita/adesso"),
    ]);
    setDati(g.data); setBozzaImp(g.data.impostazioni); setAdesso(a.data);
  }, [dal, al]);

  useEffect(() => { carica(); }, [carica]);

  const perCasella = useMemo(() => {
    const m = {};
    for (const r of dati?.assegnazioni || []) {
      (m[`${r.turno}|${r.ruolo}`] ||= []).push(r);
    }
    for (const k of Object.keys(m)) m[k].sort((x, y) => x.dal.localeCompare(y.dal));
    return m;
  }, [dati]);

  /** Stampa quello che si sta guardando: squadre e monoperatori insieme. */
  const scarica = async (come) => {
    try {
      const coda = periodo
        ? `periodo=${periodo}&riferimento=${dal}`
        : `dal=${dal}&al=${al}`;
      const r = await authFetch(`/reperibilita/${come}?${coda}`);
      if (!r.ok) {
        let msg = "Errore nella stampa";
        try { msg = (await r.json()).detail || msg; } catch { /* niente */ }
        throw new Error(msg);
      }
      const b = await r.blob();
      const u = window.URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = u;
      a.download = `reperibilita_${dal}_${al}.${come === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      window.URL.revokeObjectURL(u);
    } catch (e) { toast.error(e.message || "Errore nella stampa"); }
  };

  const salvaImp = async () => {
    try {
      const { data } = await api.put("/reperibilita/impostazioni", bozzaImp);
      setBozzaImp(data);
      toast.success("Impostazioni salvate");
      carica();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel salvataggio"); }
  };

  const togli = async (rep) => {
    try {
      await api.delete(`/reperibilita/${rep.id}`);
      carica();
    } catch { toast.error("Errore nella rimozione"); }
  };

  const tuttiRuoli = dati?.impostazioni ? adesso?.ruoli || [] : [];
  // Le squadre montano il venerdi', i monoperatori il lunedi': due tabelle,
  // perche' le settimane non si sovrappongono e affiancarle nella stessa
  // griglia farebbe leggere date sbagliate.
  const ruoli = tuttiRuoli.filter(r => !r.multiplo);
  const ruoliMono = tuttiRuoli.filter(r => r.multiplo);
  // Una tabella sola, disegnata due volte: le squadre sui turni del
  // venerdi', i monoperatori su quelli del lunedi'. Sono due calendari
  // diversi e non possono stare nelle stesse righe.
  const Tabella = ({ righeTurni, colonne }) => (
    <Card className="border-slate-200">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500">
                <tr>
                  <th className="text-left p-3 sticky left-0 bg-slate-50 min-w-[150px]">Turno</th>
                  {colonne.map(r => (
                    <th key={r.codice} className="text-left p-3 min-w-[210px]">
                      {r.nome}
                      <div className="font-normal text-[10px] text-slate-400">
                        {r.multiplo ? "un numero per postazione" : r.telefono_turno}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {righeTurni.map(t => (
                  <tr key={t} className="border-t border-slate-100 align-top">
                    <td className="p-3 sticky left-0 bg-white">
                      <div className="font-semibold">{breve(t)} → {breve(agg(t, 6))}</div>
                      <div className="text-[11px] text-slate-400">ven → gio</div>
                    </td>
                    {colonne.map(r => {
                      const celle = perCasella[`${t}|${r.codice}`] || [];
                      return (
                        <td key={r.codice} className="p-2" data-testid={`cella-${t}-${r.codice}`}>
                          {celle.length === 0 ? (
                            <button type="button" onClick={() => setScelta({ turno: t, ruolo: r.codice })}
                                    className="w-full text-left text-xs text-slate-400 border border-dashed
                                               border-slate-200 rounded px-2 py-2 hover:bg-slate-50">
                              <Plus className="w-3 h-3 inline mr-1" /> assegna
                            </button>
                          ) : (
                            <div className="space-y-1">
                              {celle.map(c => (
                                <div key={c.id}
                                     className={`rounded px-2 py-1.5 text-xs border ${
                                       c.conflitto_assenza
                                         ? "border-red-300 bg-red-50"
                                         : c.subentra_a
                                           ? "border-orange-200 bg-orange-50"
                                           : "border-emerald-200 bg-emerald-50"}`}>
                                  <div className="flex items-start justify-between gap-1">
                                    <span className="font-medium">{c.employee_name}</span>
                                    <button type="button" onClick={() => togli(c)}
                                            className="text-slate-400 hover:text-red-600"
                                            title="Togli dal turno">
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                  <div className="text-[11px] text-slate-500">
                                    {c.dal === t && c.al === agg(t, 6)
                                      ? "tutto il turno"
                                      : `${GIORNI[new Date(`${c.dal}T00:00:00`).getDay()]} → ${GIORNI[new Date(`${c.al}T00:00:00`).getDay()]}`}
                                  </div>
                                  {c.postazione && (
                                    <div className="text-[11px] text-slate-600 font-medium">
                                      {c.postazione.nome}
                                      {c.postazione.telefono && (
                                        <a href={`tel:${c.postazione.telefono.replace(/\s/g, "")}`}
                                           className="ml-1 text-[#0067b2] hover:underline font-normal">
                                          {c.postazione.telefono}
                                        </a>
                                      )}
                                    </div>
                                  )}
                                  {c.mezzo && (
                                    <div className="text-[11px] text-slate-500">{c.mezzo.identifier}</div>
                                  )}
                                  {c.subentra_a && (
                                    <div className="text-[11px] text-orange-700">
                                      al posto di {c.subentra_a}
                                    </div>
                                  )}
                                  {c.conflitto_assenza && (
                                    <div className="text-[11px] text-red-700 flex items-center gap-1">
                                      <AlertTriangle className="w-3 h-3" /> in {c.conflitto_assenza}
                                    </div>
                                  )}
                                  <button type="button" onClick={() => setScambio(c)}
                                          className="text-[11px] text-[#0067b2] hover:underline mt-0.5"
                                          data-testid={`scambia-${c.id}`}>
                                    <ArrowLeftRight className="w-3 h-3 inline mr-0.5" /> scambia
                                  </button>
                                </div>
                              ))}
                              <button type="button" onClick={() => setScelta({ turno: t, ruolo: r.codice })}
                                      className="text-[11px] text-slate-400 hover:text-slate-600">
                                {r.multiplo ? "aggiungi un monoperatore" : "sostituisci tutto il turno"}
                              </button>
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
    </Card>
  );


  return (
    <div className="space-y-6" data-testid="reperibilita-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0067b2] mb-2">Dipendenti</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Reperibilità</h1>
          <p className="text-sm text-slate-500 mt-1">
            Squadre da venerdì a giovedì, monoperatori da lunedì a domenica · cambio alle{" "}
            {dati?.impostazioni?.ora_cambio || "—"}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="h-11" onClick={() => scarica("pdf")}
                  data-testid="stampa-reperibilita">
            <Download className="w-4 h-4 mr-2" /> PDF
          </Button>
          <Button variant="outline" className="h-11" onClick={() => scarica("excel")}
                  data-testid="excel-reperibilita">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
          </Button>
        </div>
      </div>

      {adesso && (
        <Card className="border-slate-200 bg-slate-50">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">
              In turno adesso · turno del {breve(adesso.turno)}
              {adesso.giorno_di_cambio && (
                <span className="text-amber-700 normal-case tracking-normal">
                  {" "}· oggi si cambia alle {adesso.ora_cambio}
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {adesso.ruoli.map(r => (
                <div key={r.codice} className="bg-white border border-slate-200 rounded-lg p-3"
                     data-testid={`adesso-${r.codice}`}>
                  <div className="text-[11px] text-slate-400">{r.nome}</div>
                  {/* I monoperatori sono piu' d'uno insieme, ognuno con il
                      numero della sua postazione: un numero solo ne mostrerebbe
                      uno e nasconderebbe gli altri due. */}
                  {r.multiplo ? (
                    (r.in_turno_multipli || []).length === 0 ? (
                      <div className="text-sm text-slate-500 mt-1">nessuno in servizio</div>
                    ) : (
                      <div className="space-y-1.5 mt-1">
                        {r.in_turno_multipli.map((v, i) => (
                          <div key={i} className="border-b border-slate-100 last:border-0 pb-1 last:pb-0">
                            <a href={`tel:${(v.postazione?.telefono || "").replace(/\s/g, "")}`}
                               className="text-base font-semibold text-[#0067b2] flex items-center gap-1.5">
                              <Phone className="w-3.5 h-3.5" />{v.postazione?.telefono || "—"}
                              <span className="text-[10px] text-slate-400 font-normal">
                                {v.postazione?.nome}
                              </span>
                            </a>
                            <div className="text-sm font-medium">{v.full_name}</div>
                            <div className="text-[11px] text-slate-500">
                              {[v.mezzo?.identifier, v.committente].filter(Boolean).join(" · ") || " "}
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  ) : (<>
                    <a href={`tel:${(r.telefono_turno || "").replace(/\s/g, "")}`}
                       className="text-lg font-semibold text-[#0067b2] flex items-center gap-1.5">
                      <Phone className="w-4 h-4" />{r.telefono_turno || "—"}
                    </a>
                    <div className="text-sm mt-1">
                      {r.in_turno
                        ? <span className="font-medium">{r.in_turno.full_name}</span>
                        : <span className="text-red-600">nessuno assegnato</span>}
                      {r.in_turno?.subentra_a && (
                        <span className="text-xs text-orange-600"> · al posto di {r.in_turno.subentra_a}</span>
                      )}
                    </div>
                  </>)}
                  {r.monta && (
                    <div className="text-xs text-slate-500 mt-1 border-t border-slate-100 pt-1">
                      dalle {adesso.ora_cambio} monta <strong>{r.monta.full_name}</strong>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => passo(-1)}
                data-testid="turni-indietro">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-sm text-slate-600">
          {periodo && estremi ? estremi.etichetta : `dal ${breve(dal)} al ${breve(al)}`}
        </span>
        <Button variant="outline" size="sm" onClick={() => passo(1)}
                data-testid="turni-avanti">
          <ChevronRight className="w-4 h-4" />
        </Button>
        <Button variant="outline" size="sm" onClick={() => setDa(inizioTurno(iso(new Date())))}>
          Oggi
        </Button>
        <select value={periodo || String(settimane)} data-testid="periodo-reperibilita"
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^\d+$/.test(v)) { setPeriodo(""); setSettimane(Number(v)); }
                  else setPeriodo(v);
                }}
                className="h-9 border border-slate-200 rounded px-2 text-sm">
          <optgroup label="A turni">
            {[4, 6, 8, 13].map(n => (
              <option key={n} value={String(n)}>{n} turni</option>
            ))}
          </optgroup>
          <optgroup label="Periodo di calendario">
            <option value="mese">Mese</option>
            <option value="trimestre">Trimestre</option>
            <option value="semestre">Semestre</option>
            <option value="anno">Anno</option>
          </optgroup>
        </select>
        {periodo && estremi && (
          <span className="text-xs text-slate-400">
            dal {breve(estremi.dal)} al {breve(estremi.al)}
          </span>
        )}
      </div>

      <Tabella righeTurni={dati?.turni || []} colonne={ruoli} />

      {ruoliMono.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-semibold text-slate-700">
            Monoperatori
            <span className="font-normal text-slate-500"> · settimane da lunedì a domenica</span>
          </div>
          <Tabella righeTurni={dati?.turni_mono || []} colonne={ruoliMono} />
        </div>
      )}

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <button type="button" onClick={() => setMostraImp(v => !v)}
                  className="flex items-center gap-2 text-sm font-semibold text-slate-700"
                  data-testid="mostra-imp-reperibilita">
            <Settings2 className="w-4 h-4 text-[#0067b2]" /> Numeri di turno e ora del cambio
          </button>
          {mostraImp && bozzaImp && (
            <div className="mt-4 space-y-3">
              <p className="text-xs text-slate-500">
                Sono le SIM aziendali che passano di mano al cambio turno, non i cellulari
                delle persone.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[["telefono_autista", "Telefono autisti"],
                  ["telefono_saldatore", "Telefono saldatori"],
                  ["telefono_escavatorista", "Telefono escavatoristi"],
                  ["ora_cambio", "Ora del cambio"]].map(([k, label]) => (
                  <div key={k}>
                    <Label className="text-xs">{label}</Label>
                    <Input value={bozzaImp[k] || ""} disabled={user?.role !== "admin"}
                           onChange={(e) => setBozzaImp({ ...bozzaImp, [k]: e.target.value })}
                           data-testid={`imp-${k}`} className="mt-1" />
                  </div>
                ))}
              </div>
              {/* Le postazioni dei monoperatori: il numero e' del servizio e
                  resta lo stesso al cambio della persona, quindi cambiandolo
                  qui si aggiorna anche sui turni gia' scritti. */}
              <div className="pt-2 border-t border-slate-200">
                <Label className="text-xs">Postazioni monoperatori</Label>
                <p className="text-[11px] text-slate-500 mb-2">
                  Un numero per postazione, non per persona. Chi è di turno porta quel telefono.
                  Rinominarle non stacca i turni già scritti.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {(bozzaImp.postazioni_mono || []).map((post, i) => {
                    const aggiorna = (campo, valore) => {
                      const copia = [...bozzaImp.postazioni_mono];
                      copia[i] = { ...post, [campo]: valore };
                      setBozzaImp({ ...bozzaImp, postazioni_mono: copia });
                    };
                    return (
                      <div key={post.codice} className="space-y-1">
                        <Input value={post.nome || ""} disabled={user?.role !== "admin"}
                               placeholder="nome postazione"
                               data-testid={`imp-postazione-nome-${post.codice}`}
                               onChange={(e) => aggiorna("nome", e.target.value)}
                               className="font-medium" />
                        <Input value={post.telefono || ""} disabled={user?.role !== "admin"}
                               placeholder="numero dedicato"
                               data-testid={`imp-postazione-${post.codice}`}
                               onChange={(e) => aggiorna("telefono", e.target.value)} />
                      </div>
                    );
                  })}
                </div>
              </div>

              {user?.role === "admin" && (
                <Button onClick={salvaImp} className="bg-[#0067b2] hover:bg-[#00559a] text-white"
                        data-testid="salva-imp-reperibilita">
                  <Save className="w-4 h-4 mr-2" /> Salva
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {scelta && (
        <SceltaDialog scelta={scelta} postazioni={dati?.impostazioni?.postazioni_mono}
                      onClose={() => setScelta(null)}
                      onSaved={() => { setScelta(null); carica(); }} />
      )}
      {scambio && (
        <ScambioDialog rep={scambio} onClose={() => setScambio(null)}
                       onSaved={() => { setScambio(null); carica(); }} />
      )}
    </div>
  );
}

/** Chi mettere sul posto: prima chi ha la mansione e ne ha fatti meno. */
function SceltaDialog({ scelta, postazioni, onClose, onSaved }) {
  const [cand, setCand] = useState(null);
  const [mezzi, setMezzi] = useState([]);
  const [busy, setBusy] = useState(false);
  // Il monoperatore esce su una postazione, che porta con se' il telefono, e
  // con la sua macchina. Se la casella e' gia' occupata si riparte da quella.
  const mono = scelta.ruolo === "monoperatore";
  const [postazione, setPostazione] = useState(scelta.postazione || "");
  const [mezzo, setMezzo] = useState(scelta.equipment_id || "");
  const [committente, setCommittente] = useState("");
  const [committenti, setCommittenti] = useState([]);
  // Con un'anagrafica intera fra cui scegliere, scorrere l'elenco a occhio non
  // e' praticabile: si cerca per nome o per mansione.
  const [cerca, setCerca] = useState("");

  useEffect(() => {
    api.get(`/reperibilita/candidati?ruolo=${scelta.ruolo}&turno=${scelta.turno}`)
      .then(({ data }) => setCand(data)).catch(() => setCand([]));
  }, [scelta]);

  useEffect(() => {
    if (!mono) return;
    api.get("/equipment").then(({ data }) => setMezzi(data)).catch(() => {});
    api.get("/committenti").then(({ data }) => setCommittenti(data)).catch(() => {});
  }, [mono]);

  const visibili = (cand || []).filter(c => {
    const q = cerca.trim().toLowerCase();
    // Si cerca anche per mestiere, compresi quelli secondari: «escavator»
    // deve trovare l'autista che guida anche l'escavatore.
    return !q || [c.full_name, ...(c.mansioni || [c.role])]
      .filter(Boolean).join(" ").toLowerCase().includes(q);
  });

  const assegna = async (id) => {
    if (mono && !postazione) { toast.error("Scegli prima la postazione"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/reperibilita/assegna", {
        turno: scelta.turno, ruolo: scelta.ruolo, employee_id: id,
        postazione: mono ? postazione : null,
        equipment_id: mono ? (mezzo || null) : null,
        client_id: mono ? (committente || null) : null,
      });
      if (data.spostato_da) toast.success(`Spostato da ${data.spostato_da}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nell'assegnazione"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Turno del {breve(scelta.turno)} · {scelta.ruolo}</DialogTitle>
        </DialogHeader>
        {mono && (
          <div className="p-3 bg-slate-50 border border-slate-200 rounded space-y-2">
            <div>
              <Label className="text-xs">Postazione *</Label>
              <select className="w-full h-10 border border-slate-200 rounded px-3 mt-1 bg-white"
                      value={postazione} onChange={(e) => setPostazione(e.target.value)}
                      data-testid="scelta-postazione">
                <option value="">Scegli la postazione…</option>
                {(postazioni || []).map(p => (
                  <option key={p.codice} value={p.codice}>
                    {p.nome}{p.telefono ? ` — ${p.telefono}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Mezzo</Label>
              <select className="w-full h-10 border border-slate-200 rounded px-3 mt-1 bg-white"
                      value={mezzo} onChange={(e) => setMezzo(e.target.value)}>
                <option value="">Quello dell'ultima volta</option>
                {mezzi.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.identifier}{m.brand ? ` — ${m.brand} ${m.model || ""}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Committente</Label>
              <select className="w-full h-10 border border-slate-200 rounded px-3 mt-1 bg-white"
                      value={committente} onChange={(e) => setCommittente(e.target.value)}
                      data-testid="scelta-committente-mono">
                <option value="">Non indicato</option>
                {committenti.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <p className="text-[11px] text-slate-500">
              Il telefono è della postazione e resta lo stesso al cambio di persona.
              La macchina è della persona: lasciando vuoto si riprende quella dell'ultima volta.
            </p>
          </div>
        )}
        {cand === null && <p className="text-sm text-slate-400 py-6">Caricamento…</p>}
        {cand !== null && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input className="pl-9 h-10" placeholder="Cerca per nome o mansione..."
                   value={cerca} onChange={(e) => setCerca(e.target.value)}
                   data-testid="cerca-operatore" />
          </div>
        )}
        <div className="space-y-1">
          {cand !== null && visibili.length === 0 && (
            <p className="text-sm text-slate-500 py-4 text-center">Nessuno con questo nome</p>
          )}
          {visibili.map(c => (
            <button key={c.id} type="button" disabled={busy} onClick={() => assegna(c.id)}
                    data-testid={`candidato-${c.id}`}
                    className={`w-full text-left border rounded px-3 py-2 text-sm transition-colors ${
                      c.adatto ? "border-slate-200 hover:bg-slate-50"
                               : "border-slate-100 bg-slate-50 text-slate-400 hover:bg-slate-100"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium">{c.full_name}</span>
                <span className="text-xs text-slate-500">{c.turni_anno} turni quest'anno</span>
              </div>
              <div className="text-xs text-slate-500 flex flex-wrap gap-x-2">
                {/* Tutti i mestieri: e' il motivo per cui questa persona e'
                    in elenco, e con la sola mansione contrattuale non si
                    capiva perche' un «Operatore Conduzione Mezzi» comparisse
                    fra gli escavatoristi. */}
                <span>{(c.mansioni || [c.role]).filter(Boolean).join(" · ")}</span>
                {c.assente && <span className="text-red-600">in {c.assente} in questo turno</span>}
                {c.in_ufficio && <span className="text-slate-500">lavora in ufficio</span>}
                {c.pratica_aperta && (
                  <span className="text-red-600">
                    pratica di assunzione da concludere
                    {c.manca_pratica?.length > 0
                      && ` · manca ${c.manca_pratica.join(", ").toLowerCase()}`}
                  </span>
                )}
                {c.assunto_dal && (
                  <span className="text-sky-700">
                    entra in azienda il {c.assunto_dal.split("-").reverse().join("/")}
                  </span>
                )}
                {c.gia_in_turno && <span className="text-amber-700">già di turno in un altro ruolo</span>}
              </div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Cedere una parte del turno. Un giorno solo o la settimana intera sono la
 * stessa operazione: cambia quanto si cede.
 */
function ScambioDialog({ rep, onClose, onSaved }) {
  const [dal, setDal] = useState(rep.dal);
  const [al, setAl] = useState(rep.al);
  const [chi, setChi] = useState("");
  const [motivo, setMotivo] = useState("");
  const [cand, setCand] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/reperibilita/candidati?ruolo=${rep.ruolo}&turno=${rep.turno}`)
      .then(({ data }) => setCand(data.filter(c => c.id !== rep.employee_id)))
      .catch(() => setCand([]));
  }, [rep]);

  const salva = async () => {
    if (!chi) { toast.error("Scegli chi subentra"); return; }
    if (!motivo.trim()) { toast.error("Indica il motivo dello scambio"); return; }
    setBusy(true);
    try {
      await api.post(`/reperibilita/${rep.id}/scambio`,
                     { employee_id: chi, dal, al, motivo: motivo.trim() });
      toast.success("Scambio registrato");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nello scambio"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Scambio · {rep.employee_name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2">
            Copre dal {rep.dal} al {rep.al}. Restringi le date per cedere solo qualche giorno;
            lasciale così per cedere tutto il periodo.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Cede dal</Label>
              <Input type="date" value={dal} min={rep.dal} max={rep.al}
                     onChange={(e) => setDal(e.target.value)} data-testid="scambio-dal" /></div>
            <div><Label className="text-xs">al</Label>
              <Input type="date" value={al} min={dal} max={rep.al}
                     onChange={(e) => setAl(e.target.value)} data-testid="scambio-al" /></div>
          </div>
          <div>
            <Label className="text-xs">Subentra</Label>
            <select value={chi} onChange={(e) => setChi(e.target.value)}
                    data-testid="scambio-chi"
                    className="w-full h-10 border border-slate-200 rounded px-2 text-sm mt-1">
              <option value="">Scegli…</option>
              {cand.map(c => (
                <option key={c.id} value={c.id}>
                  {c.full_name}{c.adatto ? "" : ` (${c.role})`}
                  {c.assente ? ` — in ${c.assente}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Motivo <span className="text-red-500">*</span></Label>
            <Input value={motivo} onChange={(e) => setMotivo(e.target.value)}
                   placeholder="es. impegno familiare" className="mt-1"
                   data-testid="scambio-motivo" />
          </div>
          <p className="text-[11px] text-slate-500">
            Resta scritto chi ha ceduto a chi, quando e perché: fra sei mesi serve sapere
            com'era il turno quel giorno, non com'è adesso.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={salva} disabled={busy} data-testid="scambio-salva"
                  className="bg-orange-500 hover:bg-orange-600 text-white">
            {busy ? "Salvataggio…" : "Registra scambio"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
